import { createSelector } from 'reselect';

import {
	getExareInfo,
	getLastRefund,
	getTravellers,
	getIsSplittedOrder,
	getIsCancelled
} from '../../../../Checkout/store/order/selectors';

import { AnyStateWithRefund, RefundRequestDocument } from './types';
import { getIsSelectedVoluntaryReason } from './selectedReason/selectors';
import { getNasServices } from './refundInfo/selectors';
import { ExareStatus, NASStatus } from '@websky/graphql';
import { buildRequestRefundPassengers } from '../lib/buildRequestRefundPassengers';
import { getSelectedRefundFamiliesFromPassengers } from '../lib/getSelectedRefundFamilies';
import { getRefundPassengerLabel } from '../lib/refundPassengerUtils';

export const getRefundState = (state: AnyStateWithRefund) => state.refund;

export const getRefundAutoMode = createSelector(
	getExareInfo,
	getIsSelectedVoluntaryReason,
	(eExareInfo, isSelectedVoluntaryReason) => eExareInfo.refundAutoMode && isSelectedVoluntaryReason
);

export const getChildRefundWithoutAdultAllowed = createSelector(
	getExareInfo,
	eExareInfo => eExareInfo.childRefundWithoutAdultAllowed
);

export const getRefundAcceptStepEnabled = createSelector(
	getExareInfo,
	eExareInfo => eExareInfo.refundAcceptStepEnabled
);

export const getTicketPrice = createSelector(
	getTravellers,
	travellers => travellers[0].fares[0].passengerFares[0].totalFare
);

export const getIsNasServiceRefundRequired = createSelector(
	getNasServices,
	nas =>
		nas?.selectedServices?.some(selected =>
			selected.orders.some(order => order.status === NASStatus.Confirmed && order.isRefundable)
		) ?? false
);

export const getRequestStatus = createSelector(getLastRefund, lastRefund => lastRefund?.status);

export const getIsFinishedRefund = createSelector(
	getRequestStatus,
	status => status && status === ExareStatus.Finished
);

export const getIsOpenedNewRefund = createSelector(
	getRequestStatus,
	getIsFinishedRefund,
	getIsSplittedOrder,
	getIsCancelled,
	(status, isFinishedRefund, isSplittedOrder, isCancelledOrder) => {
		const isStatusValid =
			!status || [ExareStatus.TimelimitExpired, ExareStatus.Rejected, ExareStatus.Refused].includes(status);

		const isSplittedOrderValid = isFinishedRefund && isSplittedOrder && !isCancelledOrder;

		return isStatusValid || isSplittedOrderValid;
	}
);

export const getRequestId = createSelector(getLastRefund, lastRefund => lastRefund?.id);
export const getRequestTimeLimit = createSelector(
	getLastRefund,
	lastRefund => lastRefund?.timelimit && new Date(lastRefund?.timelimit)
);

export const getRequestCreateDate = createSelector(
	getLastRefund,
	lastRefund => lastRefund?.createDate && new Date(lastRefund?.createDate)
);

export const getRequestComment = createSelector(getLastRefund, lastRefund => lastRefund?.userComment);

export const getRequestReason = createSelector(getLastRefund, lastRefund => lastRefund?.reason);
export const getRequestIsAutomaticMode = createSelector(getLastRefund, lastRefund => lastRefund?.automaticMode);

/** Refund-модель пассажиров заявки: `lastRefund.travellers` + семейные связи. */
export const getRequestRefundPassengers = createSelector(getLastRefund, lastRefund =>
	buildRequestRefundPassengers(lastRefund?.travellers ?? [])
);

/** Пассажиры заявки, сгруппированные по семьям (для breadcrumbs, labels). */
export const getRequestRefundFamilies = createSelector(
	getRequestRefundPassengers,
	getSelectedRefundFamiliesFromPassengers
);

/** Загруженные документы заявки с именами пассажиров. */
export const getRequestDocuments = createSelector(
	getLastRefund,
	getRequestRefundPassengers,
	(lastRefund, passengers) => {
		const documents: RefundRequestDocument[] = [];

		if (!lastRefund?.loadedFiles) {
			return documents;
		}

		lastRefund.loadedFiles.forEach(file => {
			if (!file?.passengerId) {
				return;
			}

			const passenger = passengers.find(requestPassenger => requestPassenger.id === file.passengerId);

			if (passenger) {
				documents.push({
					passengerName: getRefundPassengerLabel(passenger),
					documentType: file.type
				});
			}
		});

		return documents;
	}
);

export const getRequestPrices = createSelector(getLastRefund, lastRefund => lastRefund?.prices);

export const getRequestRefuseAllowed = createSelector(getLastRefund, lastRefund => lastRefund?.refuseAllowed ?? false);
